/** Haversine distance in kilometers between two lat/lng points. */
export function haversineKm(a, b) {
  const R = 6371;
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

function pathDistanceKm(points) {
  let total = 0;
  for (let i = 0; i < points.length - 1; i += 1) {
    total += haversineKm(points[i], points[i + 1]);
  }
  return total;
}

function permutations(arr) {
  if (arr.length <= 1) return [arr];
  const result = [];
  for (let i = 0; i < arr.length; i += 1) {
    const rest = [...arr.slice(0, i), ...arr.slice(i + 1)];
    permutations(rest).forEach((p) => result.push([arr[i], ...p]));
  }
  return result;
}

function twoOptImprove(start, end, order) {
  let best = [...order];
  let improved = true;
  while (improved) {
    improved = false;
    for (let i = 0; i < best.length - 1; i += 1) {
      for (let j = i + 1; j < best.length; j += 1) {
        const candidate = [
          ...best.slice(0, i),
          ...best.slice(i, j + 1).reverse(),
          ...best.slice(j + 1),
        ];
        if (pathDistanceKm([start, ...candidate, end]) < pathDistanceKm([start, ...best, end])) {
          best = candidate;
          improved = true;
        }
      }
    }
  }
  return best;
}

function nearestNeighborOrder(start, end, waypoints) {
  const remaining = [...waypoints];
  const order = [];
  let current = start;
  while (remaining.length) {
    let bestIdx = 0;
    let bestDist = Infinity;
    for (let i = 0; i < remaining.length; i += 1) {
      const d = haversineKm(current, remaining[i]);
      if (d < bestDist) {
        bestDist = d;
        bestIdx = i;
      }
    }
    const next = remaining.splice(bestIdx, 1)[0];
    order.push(next);
    current = next;
  }
  return twoOptImprove(start, end, order);
}

/**
 * Fixed-start / fixed-end TSP: visit every waypoint once, minimizing total distance.
 * @param {{ lat: number, lng: number }} start
 * @param {{ lat: number, lng: number }} end
 * @param {Array<{ id: string, lat: number, lng: number, label?: string }>} waypoints
 */
export function solveFixedEndpointsTsp(start, end, waypoints) {
  const n = waypoints.length;
  if (n === 0) {
    const path = [start, end];
    return { order: [], path, totalKm: pathDistanceKm(path) };
  }

  let order;
  if (n <= 9) {
    let best = null;
    let bestDist = Infinity;
    permutations(waypoints).forEach((perm) => {
      const dist = pathDistanceKm([start, ...perm, end]);
      if (dist < bestDist) {
        bestDist = dist;
        best = perm;
      }
    });
    order = best;
  } else {
    order = nearestNeighborOrder(start, end, waypoints);
  }

  const path = [start, ...order, end];
  return {
    order,
    path,
    totalKm: pathDistanceKm(path),
  };
}

export function kmToMiles(km) {
  return km * 0.621371;
}

function pathCostFromMatrix(matrix, indices) {
  let total = 0;
  for (let i = 0; i < indices.length - 1; i += 1) {
    const step = matrix[indices[i]]?.[indices[i + 1]];
    if (step == null || !Number.isFinite(step)) return Infinity;
    total += step;
  }
  return total;
}

function twoOptImproveMatrix(startIdx, endIdx, order, matrix) {
  let best = [...order];
  let improved = true;
  const evaluate = (perm) => pathCostFromMatrix(matrix, [startIdx, ...perm, endIdx]);
  while (improved) {
    improved = false;
    for (let i = 0; i < best.length - 1; i += 1) {
      for (let j = i + 1; j < best.length; j += 1) {
        const candidate = [
          ...best.slice(0, i),
          ...best.slice(i, j + 1).reverse(),
          ...best.slice(j + 1),
        ];
        if (evaluate(candidate) < evaluate(best)) {
          best = candidate;
          improved = true;
        }
      }
    }
  }
  return best;
}

/**
 * Fixed-start / fixed-end TSP on a precomputed cost matrix (e.g. driving seconds).
 * @param {number} startIdx - matrix index for start
 * @param {number} endIdx - matrix index for end
 * @param {number[]} waypointIndices - matrix indices for stops to visit
 * @param {number[][]} matrix - matrix[a][b] = cost from a to b
 * @returns {number[]} optimized waypoint indices (subset of waypointIndices)
 */
export function solveFixedEndpointsTspWithCost(startIdx, endIdx, waypointIndices, matrix) {
  const n = waypointIndices.length;
  if (n === 0) return [];
  if (n === 1) return waypointIndices;

  const evaluate = (order) => pathCostFromMatrix(matrix, [startIdx, ...order, endIdx]);

  let order;
  if (n <= 9) {
    let best = null;
    let bestCost = Infinity;
    permutations(waypointIndices).forEach((perm) => {
      const cost = evaluate(perm);
      if (cost < bestCost) {
        bestCost = cost;
        best = perm;
      }
    });
    order = best;
  } else {
    const remaining = [...waypointIndices];
    order = [];
    let current = startIdx;
    while (remaining.length) {
      let bestIdx = 0;
      let bestCost = Infinity;
      for (let i = 0; i < remaining.length; i += 1) {
        const c = matrix[current]?.[remaining[i]];
        if (c != null && c < bestCost) {
          bestCost = c;
          bestIdx = i;
        }
      }
      const next = remaining.splice(bestIdx, 1)[0];
      order.push(next);
      current = next;
    }
    order = twoOptImproveMatrix(startIdx, endIdx, order, matrix);
  }
  return order;
}
